import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaArrowLeft, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import images from '../assets/images';
import logo from '../assets/logo.png';
import SEO, { PageSEO } from '../components/SEO';
import BrandName from '../components/BrandName';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Supabase sends reset links with #access_token=...&type=recovery in the hash.
    // The JS client processes it automatically and fires PASSWORD_RECOVERY event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        setSessionReady(true);
        setInvalidToken(false);
      }
    });

    // Also check if a recovery session is already active (e.g. page refresh)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true);
      } else {
        // Give Supabase 3 seconds to process the hash before declaring it invalid
        const timer = setTimeout(() => {
          setInvalidToken(true);
        }, 3000);
        return () => clearTimeout(timer);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .required('New password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Please confirm your password'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await resetPassword(values.newPassword);
        setResetSuccess(true);
        toast.success('Password reset successfully!');
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to reset password. The link may have expired.';
        if (message.includes('expired') || message.includes('Invalid')) {
          setInvalidToken(true);
        }
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Password strength indicator
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formik.values.newPassword);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];

  return (
    <>
      <SEO {...PageSEO.resetPassword} />
      <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-800/80 to-success-900/90 z-10"></div>
        <img
          src={images.auth.login}
          alt="Fitness Journey"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-white p-12">
          <div className="max-w-md text-center">
            <img src={logo} alt="Fitnession" className="h-16 w-auto mx-auto mb-6 brightness-0 invert" />
            <h2 className="text-4xl font-bold mb-4">Create New Password</h2>
            <p className="text-xl text-white/80 mb-8">
              Choose a strong password to keep your account secure
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-md w-full">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <img src={logo} alt="Fitnession" className="h-10 w-auto" />
              <BrandName className="text-3xl" />
            </Link>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {resetSuccess ? 'Password Reset!' : invalidToken ? 'Invalid Link' : 'Reset Password'}
            </h2>
            <p className="text-gray-600">
              {resetSuccess
                ? 'Your password has been updated successfully'
                : invalidToken
                ? 'This password reset link is invalid or has expired'
                : sessionReady
                ? 'Enter your new password below'
                : 'Verifying your reset link...'
              }
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {!sessionReady && !invalidToken ? (
              /* Loading — waiting for Supabase to process the hash */
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Verifying your reset link...</p>
              </div>
            ) : resetSuccess ? (
              /* Success State */
              <div className="text-center">
                <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaCheckCircle className="text-4xl text-success-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">All Done!</h3>
                <p className="text-gray-600 mb-6">
                  Your password has been reset successfully. You can now log in with your new password.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  Sign In Now
                </Link>
              </div>
            ) : invalidToken ? (
              /* Invalid Token State */
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaExclamationTriangle className="text-4xl text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Link Expired</h3>
                <p className="text-gray-600 mb-6">
                  This password reset link has expired or is invalid. Please request a new one.
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors mb-4"
                >
                  Request New Link
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  <FaArrowLeft className="mr-2" />
                  Back to Login
                </Link>
              </div>
            ) : (
              /* Form State */
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* New Password Field */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400" />
                    </div>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      className={`block w-full pl-10 pr-10 py-3 border ${
                        formik.touched.newPassword && formik.errors.newPassword
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="Enter new password"
                      {...formik.getFieldProps('newPassword')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="text-gray-400 hover:text-gray-600" />
                      ) : (
                        <FaEye className="text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {formik.touched.newPassword && formik.errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{formik.errors.newPassword}</p>
                  )}

                  {/* Password Strength Indicator */}
                  {formik.values.newPassword && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded ${
                              i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        Password strength: <span className="font-medium">{strengthLabels[passwordStrength - 1] || 'Very Weak'}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`block w-full pl-10 pr-10 py-3 border ${
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="Confirm new password"
                      {...formik.getFieldProps('confirmPassword')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <FaEyeSlash className="text-gray-400 hover:text-gray-600" />
                      ) : (
                        <FaEye className="text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{formik.errors.confirmPassword}</p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Password must contain:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-center">
                      <FaCheckCircle className={`mr-2 ${formik.values.newPassword.length >= 8 ? 'text-success-500' : 'text-gray-300'}`} />
                      At least 8 characters
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className={`mr-2 ${/[a-z]/.test(formik.values.newPassword) ? 'text-success-500' : 'text-gray-300'}`} />
                      One lowercase letter
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className={`mr-2 ${/[A-Z]/.test(formik.values.newPassword) ? 'text-success-500' : 'text-gray-300'}`} />
                      One uppercase letter
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className={`mr-2 ${/[0-9]/.test(formik.values.newPassword) ? 'text-success-500' : 'text-gray-300'}`} />
                      One number
                    </li>
                  </ul>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Resetting Password...
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                {/* Back to Login Link */}
                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
                  >
                    <FaArrowLeft className="mr-2" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>

          {/* Trust Badges */}
          <div className="mt-8 flex items-center justify-center space-x-6 text-sm text-gray-500">
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-2" />
              <span>Secure Reset</span>
            </div>
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-2" />
              <span>256-bit SSL</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ResetPassword;
